import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { gettingDocumentTypeEmployee } from "utils/Employees";
import { getTimezones, Timezone } from "utils/Timezones";
import { compareObjects } from "utils/Validations";
import { TimekeeperService } from "services/TimekeeperService";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

type Country = { code: string; label: string; phone: string };
type PayRuleOption = { pay_rule_id: number; name: string };

const DEFAULT_COUNTRY: Country = { code: "CL", label: "Chile", phone: "56" };
const DEFAULT_DOCUMENT_TYPE = "3";
const DEFAULT_TIMEZONE = "America/Santiago";

const EMPTY_STATE = {
	firtsName: "",
	lastName: "",
	birthDate: null as Dayjs | null,
	email: "",
	phoneNumber: "",
	address: "",
	documentEmployee: "",
	payRule: "" as number | string,
	typeDocument: DEFAULT_DOCUMENT_TYPE as number | string,
	country: DEFAULT_COUNTRY,
	timezone: DEFAULT_TIMEZONE,
};

const toDayjs = (value: unknown): Dayjs | null => {
	if (value === null || value === undefined || value === "") {
		return null;
	}

	if (dayjs.isDayjs(value)) {
		return value;
	}

	if (typeof value === "string") {
		const parsed = dayjs(value, "YYYY-MM-DD", true);
		if (parsed.isValid()) {
			return parsed;
		}
	}

	const parsed = dayjs(value as string | number | Date);
	return parsed.isValid() ? parsed : null;
};

export interface UseEmployeeFormParams {
	open: boolean;
	setOpen: (open: boolean) => void;
	/** null para Add, objeto empleado para Edit */
	initialData?: Employee | null;
	/** filas completas de empleados para validar número de documento duplicado */
	rows?: Employee[];
}

export interface UseEmployeeFormReturn {
	firtsName: string;
	setFirtsName: (v: string) => void;
	lastName: string;
	setLastName: (v: string) => void;
	birthDate: Dayjs | null;
	setBirthDate: (v: Dayjs | null) => void;
	email: string;
	setEmail: (v: string) => void;
	phoneNumber: string;
	setPhoneNumber: (v: string) => void;
	address: string;
	setAddress: (v: string) => void;
	documentEmployee: string;
	setDocumentEmployee: (v: string) => void;
	payRule: number | string;
	setPayRule: (v: number | string) => void;
	documentType: number | string;
	setDocumentType: (v: number | string) => void;
	country: Country | null;
	setCountry: (v: Country | null) => void;
	timezone: string;
	setTimezone: (v: string) => void;

	payRules: PayRuleOption[];
	documentTypes: DocumentTyp[];
	timezones: Timezone[];

	loadingSubmit: boolean;
	setLoadingSubmit: (v: boolean) => void;
	loadingPayRule: boolean;
	loadingDocumentType: boolean;

	alert: boolean;
	setAlert: (v: boolean) => void;

	existPersonNumber: () => boolean;
	closing: (skip: boolean) => void;
	getTooltip: () => string[] | string;
	isDisabled: boolean;
	getBody: (momentBirthDate: string) => Omit<EmployeeBase, "pay_period_end">;
}

export function useEmployeeForm({
	open,
	setOpen,
	initialData = null,
	rows = [],
}: UseEmployeeFormParams): UseEmployeeFormReturn {
	const { t } = useTranslation();
	const tk = useMemo(() => new TimekeeperService(), []);
	const timezones = useMemo(() => getTimezones(), []);

	const [firtsName, setFirtsName] = useState("");
	const [lastName, setLastName] = useState("");
	const [birthDate, setBirthDate] = useState<Dayjs | null>(null);
	const [email, setEmail] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [address, setAddress] = useState("");
	const [documentEmployee, setDocumentEmployee] = useState("");
	const [payRules, setPayRules] = useState<PayRuleOption[]>([]);
	const [payRule, setPayRule] = useState<number | string>("");
	const [documentTypes, setDocumentTypes] = useState<DocumentTyp[]>([]);
	const [documentType, setDocumentType] = useState<number | string>(DEFAULT_DOCUMENT_TYPE);
	const [country, setCountry] = useState<Country | null>(DEFAULT_COUNTRY);
	const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);

	const [alert, setAlert] = useState(false);
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const [loadingPayRule, setLoadingPayRule] = useState(false);
	const [loadingDocumentType, setLoadingDocumentType] = useState(false);

	useEffect(() => {
		if (!open) return;

		setLoadingPayRule(true);
		tk.payrules
			.getPayRules()
			.then(setPayRules)
			.catch(console.error)
			.finally(() => setLoadingPayRule(false));

		gettingDocumentTypeEmployee(setLoadingDocumentType, setDocumentTypes);

		if (initialData) {
			setFirtsName(initialData.first_name ?? "");
			setLastName(initialData.last_name ?? "");
			setBirthDate(toDayjs(initialData.birth_date));
			setEmail(initialData.email ?? "");
			setPhoneNumber(initialData.phone_number ?? "");
			setAddress(initialData.address ?? "");
			setDocumentEmployee(initialData.document_employee ?? "");
			setPayRule(initialData.pay_rule_id ?? "");
			setDocumentType(initialData.document_type_id ?? DEFAULT_DOCUMENT_TYPE);
			setTimezone((initialData as Employee & { timezone?: string }).timezone ?? DEFAULT_TIMEZONE);
		}
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	const resetForm = useCallback(() => {
		setFirtsName("");
		setLastName("");
		setBirthDate(null);
		setEmail("");
		setPhoneNumber("");
		setAddress("");
		setDocumentEmployee("");
		setPayRules([]);
		setPayRule("");
		setDocumentType(DEFAULT_DOCUMENT_TYPE);
		setCountry(DEFAULT_COUNTRY);
		setTimezone(DEFAULT_TIMEZONE);
		setAlert(false);
		setLoadingSubmit(false);
		setLoadingPayRule(false);
	}, []);

	const existPersonNumber = useCallback((): boolean => {
		const otherRows = initialData
			? rows.filter((row) => row.employee_id !== initialData.employee_id)
			: rows;
		return otherRows.some((row) => row.document_employee === documentEmployee);
	}, [documentEmployee, initialData, rows]);

	const closing = useCallback(
		(skip: boolean) => {
			if (!skip) {
				const initial = initialData
					? {
							firtsName: initialData.first_name,
							lastName: initialData.last_name,
							birthDate: toDayjs(initialData.birth_date),
							email: initialData.email,
							phoneNumber: initialData.phone_number,
							address: initialData.address,
							documentEmployee: initialData.document_employee,
							payRule: initialData.pay_rule_id,
							typeDocument: initialData.document_type_id,
							country: DEFAULT_COUNTRY,
							timezone: (initialData as Employee & { timezone?: string }).timezone ?? DEFAULT_TIMEZONE,
					  }
					: EMPTY_STATE;

				const actual = {
					firtsName,
					lastName,
					birthDate,
					email,
					phoneNumber,
					address,
					documentEmployee,
					payRule,
					typeDocument: documentType,
					country,
					timezone,
				};

				if (!compareObjects(initial, actual)) {
					setAlert(true);
					return;
				}
			}

			setOpen(false);
			resetForm();
		},
		[
			initialData,
			firtsName, lastName, birthDate, email, phoneNumber,
			address, documentEmployee, payRule, documentType, country, timezone,
			setOpen, resetForm,
		]
	);

	const getTooltip = useCallback((): string[] | string => {
		const ns = initialData
			? "timekeeper.employees.dialog.edit.tooltips"
			: "timekeeper.employees.dialog.add.tooltips";

		const fields: string[] = [];
		if (!firtsName) fields.push(t(`${ns}.name`));
		if (!lastName) fields.push(t(`${ns}.last_name`));
		if (!birthDate) fields.push(t(`${ns}.birth_date`));
		if (!email) fields.push(t(`${ns}.email`));
		if (!phoneNumber) fields.push(t(`${ns}.phone`));
		if (phoneNumber.length < 4) fields.push(t("timekeeper.employees.dialog.add.tooltips.phone_min_4"));
		if (!address) fields.push(t(`${ns}.address`));
		if (address.length < 4) fields.push(t("timekeeper.employees.dialog.add.tooltips.address_min_4"));
		if (!documentEmployee) fields.push(t(`${ns}.document_number`));
		if (!payRule) fields.push(t(`${ns}.select_pay_rule`));
		if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email))
			fields.push(t("timekeeper.employees.dialog.edit.tooltips.invalid_email"));
		if (existPersonNumber())
			fields.push(t("timekeeper.employees.dialog.edit.tooltips.document_exists"));

		if (fields.length === 0) return "";
		return fields;
	}, [
		initialData, t, firtsName, lastName, birthDate, email,
		phoneNumber, address, documentEmployee, payRule, existPersonNumber,
	]);

	const isDisabled =
		!firtsName || !lastName || !payRule || !documentEmployee ||
		!email || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email) ||
		!phoneNumber || phoneNumber.length < 4 ||
		!address || address.length < 4 ||
		!birthDate || existPersonNumber();

	const getBody = useCallback(
		(momentBirthDate: string): Omit<EmployeeBase, "pay_period_end"> => ({
			first_name: firtsName,
			last_name: lastName,
			birth_date: momentBirthDate as unknown as Date,
			email,
			phone_number: phoneNumber,
			address,
			document_type_id: parseInt(String(documentType)),
			document_employee: documentEmployee,
			pay_rule_id: payRule as number | null,
			timezone,
		} as Omit<EmployeeBase, "pay_period_end">),
		[firtsName, lastName, email, phoneNumber, address, documentType, documentEmployee, payRule, timezone]
	);

	return {
		firtsName, setFirtsName,
		lastName, setLastName,
		birthDate, setBirthDate,
		email, setEmail,
		phoneNumber, setPhoneNumber,
		address, setAddress,
		documentEmployee, setDocumentEmployee,
		payRule, setPayRule,
		documentType, setDocumentType,
		country, setCountry,
		timezone, setTimezone,
		payRules,
		documentTypes,
		timezones,
		loadingSubmit, setLoadingSubmit,
		loadingPayRule,
		loadingDocumentType,
		alert, setAlert,
		existPersonNumber,
		closing,
		getTooltip,
		isDisabled,
		getBody,
	};
}
